import React, { useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2, Mail, MapPin, MessageCircle, Send, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { inputClass, labelClass, btnPrimary } from "@/components/Brand";
import { PageLayout } from "@/components/PageLayout";

const SUPPORT_EMAIL = "seleksa.com@gmail.com";
const WHATSAPP_DISPLAY = "+62 822-7368-0480";
const WHATSAPP_DIGITS = "6282273680480";
const HQ_ADDRESS = "Jl. Pasar 1, Komp SBE (Setia Budi Estate), Kota Medan, Sumatera Utara";

const SUBJECTS = ["sales", "support", "billing", "partnership", "other"];

const emptyForm = { name: "", email: "", subject: "sales", message: "" };
const EMAIL_RE = /^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/;

const ContactChannel = ({ icon: Icon, label, children, testId }) => (
  <div className="border-t border-[#1E1E1E] pt-7" data-testid={testId}>
    <div className="flex gap-4">
      <Icon size={18} strokeWidth={1.5} className="text-[#D4AF37] shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="eyebrow">{label}</p>
        <div className="mt-2.5 text-sm text-[#F3F3F1] leading-relaxed break-words">{children}</div>
      </div>
    </div>
  </div>
);

export default function ContactUs() {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = t("err_name");
    if (!EMAIL_RE.test(form.email.trim())) next.email = t("err_email");
    if (form.message.trim().length < 10) next.message = t("err_message");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject,
        message: form.message.trim(),
      });
      setForm(emptyForm);
      setErrors({});
      setSent(true);
      toast.success(t("contact_sent"));
    } catch (err) {
      toast.error(apiError(err, t("contact_failed")));
    } finally {
      setBusy(false);
    }
  };

  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <PageLayout
      testId="contact-us-page"
      eyebrow={t("nav_contact")}
      title={t("contact_title")}
      intro={t("contact_intro")}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
        {/* Left: the channels */}
        <div className="space-y-7" data-testid="contact-channels">
          <ContactChannel icon={Mail} label={t("support_email")} testId="channel-email">
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#D4AF37] hover:underline"
              data-testid="support-email-link">
              {SUPPORT_EMAIL}
            </a>
            <p className="mt-2 text-[#A8A8A8]">{t("email_note")}</p>
          </ContactChannel>

          <ContactChannel icon={MessageCircle} label={t("whatsapp")} testId="channel-whatsapp">
            <a href={`https://wa.me/${WHATSAPP_DIGITS}`} target="_blank" rel="noreferrer"
              className="text-[#D4AF37] hover:underline font-mono-accent" data-testid="whatsapp-link">
              {WHATSAPP_DISPLAY}
            </a>
            <p className="mt-2 text-[#A8A8A8]">{t("whatsapp_note")}</p>
          </ContactChannel>

          <ContactChannel icon={MapPin} label={t("hq_address")} testId="channel-address">
            <p>{HQ_ADDRESS}</p>
            <p className="mt-2 text-[#A8A8A8]">{t("address_note")}</p>
          </ContactChannel>

          <ContactChannel icon={Clock} label={t("office_hours")} testId="channel-hours">
            <p className="font-mono-accent text-xs">{t("hours_value")}</p>
            <p className="mt-2 text-[#A8A8A8]">{t("hours_note")}</p>
          </ContactChannel>
        </div>

        {/* Right: the form */}
        <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-7 lg:p-9">
          <p className="eyebrow">{t("send_message")}</p>
          <h2 className="mt-3 text-2xl tracking-tight">{t("form_heading")}</h2>

          {sent && (
            <div className="mt-6 border-l-2 border-[#D4AF37] pl-5 py-1" data-testid="contact-success-banner">
              <p className="text-sm text-[#F3F3F1]">{t("contact_sent_body")}</p>
            </div>
          )}

          <form onSubmit={submit} className="mt-7 space-y-6" data-testid="contact-form" noValidate>
            <div>
              <label className={labelClass} htmlFor="contact-name">{t("your_name")}</label>
              <input id="contact-name" data-testid="contact-name-input" className={inputClass} value={form.name}
                onChange={(e) => set({ name: e.target.value })} placeholder="Rizky Pratama" />
              {errors.name && <p className="mt-2 text-xs text-[#E06C6C]" data-testid="error-name">{errors.name}</p>}
            </div>

            <div>
              <label className={labelClass} htmlFor="contact-email">{t("email")}</label>
              <input id="contact-email" data-testid="contact-email-input" type="email" className={inputClass}
                value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="studio@example.com" />
              {errors.email && <p className="mt-2 text-xs text-[#E06C6C]" data-testid="error-email">{errors.email}</p>}
            </div>

            <div>
              <label className={labelClass} htmlFor="contact-subject">{t("subject")}</label>
              <select id="contact-subject" data-testid="contact-subject-select" value={form.subject}
                onChange={(e) => set({ subject: e.target.value })}
                className="select-dark text-base sm:text-sm w-full mt-1 min-h-[44px]">
                {SUBJECTS.map((s) => <option key={s} value={s}>{t(`subject_${s}`)}</option>)}
              </select>
            </div>

            <div>
              <label className={labelClass} htmlFor="contact-message">{t("message")}</label>
              <textarea id="contact-message" data-testid="contact-message-input" rows={6} value={form.message}
                onChange={(e) => set({ message: e.target.value })} placeholder={t("message_placeholder")}
                className="mt-1 w-full bg-[#050505] border border-[#2A2A2A] p-4 text-base sm:text-sm text-[#F3F3F1] placeholder:text-[#5A5A5A] focus:border-[#D4AF37] focus:outline-none transition-colors" />
              <div className="mt-2 flex justify-between gap-4">
                {errors.message
                  ? <p className="text-xs text-[#E06C6C]" data-testid="error-message">{errors.message}</p>
                  : <span />}
                <span className="font-mono-accent text-[10px] text-[#707070]" data-testid="message-counter">
                  {form.message.length}/4000
                </span>
              </div>
            </div>

            <button data-testid="contact-submit-btn" disabled={busy} className={`${btnPrimary} w-full py-4`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t("send_message")}
            </button>
            <p className="text-[11px] text-[#707070] leading-relaxed">{t("form_privacy_note")}</p>
          </form>
        </div>
      </div>
    </PageLayout>
  );
}
